import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { scrollBehavior } from '../utils/motion';

/**
 * SPA scroll behavior on route change:
 *   • POP (browser back/forward) — let the browser restore scroll naturally.
 *   • Push / replace WITH hash    — scroll the matching element into view.
 *   • Push / replace WITHOUT hash — scroll to top.
 *
 * For cross-page hash navigation (e.g. /adotar → /#contact), the destination
 * Home page renders <Pets /> in a loading state — the page is shorter than
 * its final height until the API resolves and adds the cards. A single
 * rAF-deferred scroll would land in the wrong place. We re-scroll on every
 * body resize for ~2s to follow the layout as it settles.
 */
const HASH_FOLLOW_WINDOW_MS = 2000;

const ScrollToTop = (): null => {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (navType === 'POP') return;

    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
      return;
    }

    const id = hash.startsWith('#') ? hash.slice(1) : hash;
    const behavior = scrollBehavior('smooth');

    const scrollToTarget = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior, block: 'start' });
    };

    const raf = requestAnimationFrame(scrollToTarget);

    if (typeof ResizeObserver === 'undefined') {
      return () => cancelAnimationFrame(raf);
    }

    const observer = new ResizeObserver(scrollToTarget);
    observer.observe(document.body);
    const stop = window.setTimeout(
      () => observer.disconnect(),
      HASH_FOLLOW_WINDOW_MS,
    );

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.clearTimeout(stop);
    };
  }, [pathname, hash, navType]);

  return null;
};

export default ScrollToTop;
